import * as React from 'react'
import { Button } from '../../../shared-components/Button'
import { ErrorMessage } from '../../../shared-components/ErrorMessage'
import { InputText } from '../../../shared-components/InputText'
import { useScreenTracking } from '../../../shared-hooks/useScreenTracking'
import { track } from '../../../utils/analytics'
import { SignupCard } from '../SignupCard'
import { SSOStepProps } from './SSOFlow'

type UberPasswordProps = SSOStepProps & {
  sessionId: string
}

export const UberPassword = ({
  sessionId,
  makeSSORequest,
}: UberPasswordProps): JSX.Element => {
  useScreenTracking('UberPasswordView', 'View uber password input screen')

  const [password, setPassword] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setErrorMessage('')

    track('UberPasswordAttempt', 'Uber password attempt')

    const body = {
      session_id: sessionId,
      stage: 'sign_in',
      event_type: 'TypeVerifyPassword',
      credentials: {
        password,
      },
    }
    try {
      await makeSSORequest(body, 'Password is incorrect. Please try again.')
      track('UberPasswordSuccess', 'Uber password success')
    } catch (error) {
      track('UberPasswordFailure', 'Uber password failure')
      setIsSubmitting(false)
      setErrorMessage(error?.message)
    }
  }

  const moveToEmailVerification = async () => {
    const body = {
      session_id: sessionId,
      stage: 'options',
      event_type: 'TypeEmailOTP',
    }

    track('UseEmailCodeInsteadClick', 'Use email code instead click')
    try {
      await makeSSORequest(
        body,
        'Something unexpected happened, please try again later.'
      )
    } catch (error) {
      setErrorMessage(error?.message)
    }
  }

  return (
    <SignupCard
      heading='Enter your password'
      subheading='Enter the password associated with your Uber account. Your data is encrypted and secure.'
    >
      <form className='flex flex-col gap-4' onSubmit={onSubmit}>
        <InputText
          type='password'
          placeholder='Password'
          autoComplete='current-password'
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={isSubmitting}
          required
        />
        <ErrorMessage>{errorMessage}</ErrorMessage>
        <button
          type='button'
          className={`text-left text-sm font-extralight py-2 pl-1 rounded text-body-black hover-hover:hover:bg-blue-50 hover-hover:hover:bg-opacity-50 active:bg-blue-50 active:bg-opacity-50${
            errorMessage ? '' : ' -mt-6'
          }`}
          onClick={moveToEmailVerification}
        >
          Email me a code instead
        </button>
        <Button type='submit' isSubmitting={isSubmitting}>
          Continue
        </Button>
      </form>
    </SignupCard>
  )
}
